import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import {
  Add,
  DeleteOutline,
  DragIndicator,
  Edit,
  ExpandLess,
  ExpandMore,
  Launch,
  Restore,
  Colorize
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ColorPicker, {
  isValidHexColor,
  normalizeHexColor,
  saveCustomColor,
} from "../../components/ColorPicker";

const emptyForm = {
  id: "",
  type: "external",
  label: "",
  icon: "🔗",
  url: "",
  section: "main",
  categoryId: "",
  isActive: true
};

const emptyCategoryForm = {
  id: "",
  name: "",
  color: "#2F80ED",
  section: "main",
  isActive: true
};

const NO_MENU_CATEGORY = "__no_menu_category__";

const sectionLabels = {
  header: "Header",
  main: "Menú principal",
  administration: "Administración"
};

const typeLabels = {
  builtin: "Sistema",
  external: "URL externa",
  embed: "Embebido"
};

const parsePermissions = value => {
  if (!value) return null;
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const useStyles = makeStyles(theme => ({
  headerContent: {
    alignItems: "center",
    display: "flex",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1)
    }
  },
  mainPaper: {
    flex: 1,
    margin: 20,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1)
    }
  },
  sections: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  headerSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    minHeight: 138,
    padding: theme.spacing(1.5)
  },
  categoryCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    overflow: "hidden",
    position: "relative"
  },
  headerCategoryCard: {
    flex: "0 0 300px",
    marginBottom: 0
  },
  categoryHeader: {
    alignItems: "center",
    cursor: "pointer",
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "auto 1fr auto auto",
    minHeight: 44,
    padding: theme.spacing(1)
  },
  categoryColor: {
    borderRadius: 999,
    height: 14,
    width: 14
  },
  categoryBody: {
    display: "grid",
    gap: theme.spacing(1),
    padding: theme.spacing(1)
  },
  headerItems: {
    display: "flex",
    gap: theme.spacing(1),
    minHeight: 72,
    overflowX: "auto",
    paddingBottom: theme.spacing(0.5),
    ...theme.scrollbarStyles
  },
  section: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    minHeight: 280,
    padding: theme.spacing(1.5)
  },
  sectionHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  items: {
    display: "grid",
    gap: theme.spacing(1)
  },
  item: {
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    cursor: "grab",
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "auto auto 1fr auto",
    minHeight: 58,
    padding: theme.spacing(1),
    position: "relative",
    transition: "border-color 160ms ease, box-shadow 160ms ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
    }
  },
  itemDropBefore: {
    boxShadow: `0 -3px 0 ${theme.palette.primary.main}, 0 4px 12px rgba(0, 0, 0, 0.10)`
  },
  itemDropAfter: {
    boxShadow: `0 3px 0 ${theme.palette.primary.main}, 0 4px 12px rgba(0, 0, 0, 0.10)`
  },
  headerItem: {
    flex: "0 0 260px"
  },
  headerDropBefore: {
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.14)",
    "&:before": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      bottom: 8,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
      content: '""',
      left: 6,
      position: "absolute",
      top: 8,
      width: 6,
      zIndex: 3
    }
  },
  headerDropAfter: {
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.14)",
    "&:before": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      bottom: 8,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
      content: '""',
      position: "absolute",
      right: 6,
      top: 8,
      width: 6,
      zIndex: 3
    }
  },
  itemDragging: {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.10)"
  },
  dragIcon: {
    color: theme.palette.text.secondary
  },
  emoji: {
    alignItems: "center",
    display: "inline-flex",
    fontSize: 22,
    justifyContent: "center",
    minWidth: 28
  },
  itemTitle: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word"
  },
  itemMeta: {
    alignItems: "center",
    color: theme.palette.text.secondary,
    display: "flex",
    flexWrap: "wrap",
    fontSize: 12,
    gap: theme.spacing(0.75),
    marginTop: 2,
    wordBreak: "break-word"
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.5)
  },
  headerSwitch: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      marginRight: 0
    }
  },
  dropArea: {
    minHeight: 72
  },
  formGrid: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "80px 1fr",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  fullWidth: {
    gridColumn: "1 / -1"
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.18)"
  },
  helper: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  empty: {
    alignItems: "center",
    color: theme.palette.text.secondary,
    display: "flex",
    justifyContent: "center",
    minHeight: 140,
    textAlign: "center"
  }
}));

const Menus = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [categoryColorPickerOpen, setCategoryColorPickerOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [resetMenuOpen, setResetMenuOpen] = useState(false);
  const [showAllNormalMenu, setShowAllNormalMenu] = useState(false);
  const [savingShowAllNormalMenu, setSavingShowAllNormalMenu] = useState(false);
  const [draggingId, setDraggingId] = useState("");
  const [draggingCategoryId, setDraggingCategoryId] = useState("");
  const [dropTarget, setDropTarget] = useState(null);
  const [categoryDropTarget, setCategoryDropTarget] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const companyPermissions = useMemo(
    () => parsePermissions(user?.company?.menuPermissions),
    [user?.company?.menuPermissions]
  );

  const canShowMenuItem = item => {
    if (item.type !== "builtin") return true;
    if (!Array.isArray(companyPermissions)) return true;
    return companyPermissions.includes(item.builtinKey);
  };

  const visibleItems = useMemo(
    () => items.filter(canShowMenuItem),
    [items, companyPermissions]
  );

  const groupedItems = useMemo(
    () => ({
      header: visibleItems.filter(item => item.section === "header"),
      main: visibleItems.filter(item => item.section === "main"),
      administration: visibleItems.filter(item => item.section === "administration")
    }),
    [visibleItems]
  );

  const publishItems = nextItems => {
    window.dispatchEvent(
      new CustomEvent("account-menu-updated", {
        detail: { items: nextItems, categories }
      })
    );
  };

  const publishMenuState = (nextItems = items, nextCategories = categories) => {
    window.dispatchEvent(
      new CustomEvent("account-menu-updated", {
        detail: { items: nextItems, categories: nextCategories }
      })
    );
  };

  const sortItems = nextItems =>
    ["header", "main", "administration"].flatMap(section =>
      nextItems
        .filter(item => item.section === section)
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
        .map((item, index) => ({ ...item, section, position: index }))
    );

  const sortCategories = nextCategories =>
    ["header", "main", "administration"].flatMap(section =>
      nextCategories
        .filter(category => category.section === section)
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
        .map((category, index) => ({ ...category, section, position: index }))
    );

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account-menu");
      const nextItems = sortItems(data.items || []);
      const nextCategories = sortCategories(data.categories || []);
      setItems(nextItems);
      setCategories(nextCategories);
      publishMenuState(nextItems, nextCategories);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    const fetchShowAllNormalMenu = async () => {
      try {
        const { data } = await api.get("/settings/normal-mode-show-all-menu");
        setShowAllNormalMenu(Boolean(data.showAll));
      } catch (err) {
        toastError(err);
      }
    };

    fetchShowAllNormalMenu();
  }, []);

  const handleToggleShowAllNormalMenu = async event => {
    const nextValue = event.target.checked;
    setShowAllNormalMenu(nextValue);
    setSavingShowAllNormalMenu(true);

    try {
      const { data } = await api.put("/settings/normal-mode-show-all-menu", {
        showAll: nextValue
      });
      const savedValue = Boolean(data.showAll);
      setShowAllNormalMenu(savedValue);
      window.dispatchEvent(
        new CustomEvent("normalModeShowAllMenuChanged", {
          detail: { showAll: savedValue }
        })
      );
      toast.success("Configuracion actualizada");
    } catch (err) {
      setShowAllNormalMenu(!nextValue);
      toastError(err);
    } finally {
      setSavingShowAllNormalMenu(false);
    }
  };

  const setLocalItems = updater => {
    setItems(prevItems => {
      const nextItems =
        typeof updater === "function" ? sortItems(updater(prevItems)) : sortItems(updater);
      publishMenuState(nextItems, categories);
      return nextItems;
    });
  };

  const setLocalCategories = updater => {
    setCategories(prevCategories => {
      const nextCategories =
        typeof updater === "function"
          ? sortCategories(updater(prevCategories))
          : sortCategories(updater);
      publishMenuState(items, nextCategories);
      return nextCategories;
    });
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(emptyForm);
  };

  const openCreateForm = () => {
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openCreateCategoryForm = () => {
    setCategoryForm(emptyCategoryForm);
    setCategoryFormOpen(true);
  };

  const openEditForm = item => {
    setForm({
      id: item.id,
      type: item.type,
      label: item.label || "",
      icon: item.icon || "🔗",
      url: item.url || "",
      section: item.section || "main",
      categoryId: item.categoryId || "",
      isActive: item.isActive !== false
    });
    setFormOpen(true);
  };

  const openEditCategoryForm = category => {
    setCategoryForm({
      id: category.id,
      name: category.name || "",
      color: category.color || "#2F80ED",
      section: category.section || "main",
      isActive: category.isActive !== false
    });
    setCategoryFormOpen(true);
  };

  const handleFormChange = field => event => {
    const value = field === "isActive" ? event.target.checked : event.target.value;
    setForm(prevState => ({
      ...prevState,
      [field]: value,
      ...(field === "section" ? { categoryId: "" } : {})
    }));
  };

  const handleCategoryFormChange = field => event => {
    const value = field === "isActive" ? event.target.checked : event.target.value;
    setCategoryForm(prevState => ({ ...prevState, [field]: value }));
  };

  const saveItem = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        label: form.label,
        icon: form.icon,
        section: form.section,
        categoryId: form.categoryId || null,
        isActive: form.isActive
      };

      if (form.type !== "builtin") {
        payload.type = form.type;
        payload.url = form.url;
      }

      if (form.id) {
        const { data } = await api.put(`/account-menu/${form.id}`, payload);
        setLocalItems(prevItems =>
          prevItems.map(item => (Number(item.id) === Number(data.id) ? data : item))
        );
      } else {
        const { data } = await api.post("/account-menu", {
          ...payload,
          type: form.type,
          url: form.url
        });
        setLocalItems(prevItems => [...prevItems, data]);
      }

      toast.success("Menú actualizado");
      closeForm();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const saveCategory = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: categoryForm.name,
        color: categoryForm.color,
        section: categoryForm.section,
        isActive: categoryForm.isActive
      };

      if (categoryForm.id) {
        const { data } = await api.put(`/account-menu-categories/${categoryForm.id}`, payload);
        setLocalCategories(prevCategories =>
          prevCategories.map(category =>
            Number(category.id) === Number(data.id) ? data : category
          )
        );
        if (data.section !== categoryForm.section) {
          setLocalItems(prevItems =>
            prevItems.map(item =>
              Number(item.categoryId) === Number(data.id)
                ? { ...item, section: data.section }
                : item
            )
          );
        }
      } else {
        const { data } = await api.post("/account-menu-categories", payload);
        setLocalCategories(prevCategories => [...prevCategories, data]);
        setExpandedCategories(prevState => ({ ...prevState, [data.id]: true }));
      }

      toast.success("Categoría actualizada");
      setCategoryFormOpen(false);
      setCategoryForm(emptyCategoryForm);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      await api.delete(`/account-menu/${deleteItem.id}`);
      setLocalItems(prevItems =>
        deleteItem.type === "builtin"
          ? prevItems.map(item =>
              Number(item.id) === Number(deleteItem.id)
                ? { ...item, isActive: false }
                : item
            )
          : prevItems.filter(item => Number(item.id) !== Number(deleteItem.id))
      );
      toast.success(deleteItem.type === "builtin" ? "Opción desactivada" : "Opción eliminada");
      setDeleteItem(null);
    } catch (err) {
      toastError(err);
    }
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCategory) return;

    try {
      await api.delete(`/account-menu-categories/${deleteCategory.id}`);
      setLocalItems(prevItems =>
        prevItems.map(item =>
          Number(item.categoryId) === Number(deleteCategory.id)
            ? { ...item, categoryId: null }
            : item
        )
      );
      setLocalCategories(prevCategories =>
        prevCategories.filter(category => Number(category.id) !== Number(deleteCategory.id))
      );
      toast.success("Categoría eliminada");
      setDeleteCategory(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleResetMenu = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/account-menu/reset");
      const nextItems = sortItems(data.items || []);
      const nextCategories = sortCategories(data.categories || []);

      setItems(nextItems);
      setCategories(nextCategories);
      setExpandedCategories({});
      publishMenuState(nextItems, nextCategories);
      setResetMenuOpen(false);
      toast.success("Menú restablecido");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const persistOrder = async nextItems => {
    const payload = nextItems.map(item => ({
      id: item.id,
      section: item.section,
      categoryId: item.categoryId || null,
      position: item.position
    }));

    try {
      const { data } = await api.put("/account-menu/reorder", { items: payload });
      setLocalItems(data.items || nextItems);
    } catch (err) {
      toastError(err);
      fetchItems();
    }
  };

  const persistCategoryOrder = async nextCategories => {
    const payload = nextCategories.map(category => ({
      id: category.id,
      section: category.section,
      position: category.position
    }));

    try {
      const { data } = await api.put("/account-menu-categories/reorder", {
        categories: payload
      });
      setLocalCategories(data.categories || nextCategories);
      if (data.items) setLocalItems(data.items);
    } catch (err) {
      toastError(err);
      fetchItems();
    }
  };

  const moveCategory = (targetSection, targetId = "", placement = "after") => {
    if (!draggingCategoryId) return;

    const draggedCategory = categories.find(
      category => String(category.id) === String(draggingCategoryId)
    );
    if (!draggedCategory) return;

    const withoutDragged = categories.filter(
      category => String(category.id) !== String(draggingCategoryId)
    );
    const nextSections = {
      header: withoutDragged.filter(category => category.section === "header"),
      main: withoutDragged.filter(category => category.section === "main"),
      administration: withoutDragged.filter(category => category.section === "administration")
    };
    const nextDraggedCategory = { ...draggedCategory, section: targetSection };
    const targetList = nextSections[targetSection];
    const targetIndex = targetId
      ? targetList.findIndex(category => String(category.id) === String(targetId))
      : targetList.length;
    const insertIndex =
      targetIndex >= 0
        ? targetIndex + (placement === "after" ? 1 : 0)
        : targetList.length;

    targetList.splice(insertIndex, 0, nextDraggedCategory);

    const nextCategories = ["header", "main", "administration"].flatMap(section =>
      nextSections[section].map((category, index) => ({
        ...category,
        section,
        position: index
      }))
    );

    setLocalCategories(nextCategories);
    persistCategoryOrder(nextCategories);
    setDraggingCategoryId("");
    setCategoryDropTarget(null);
  };

  const moveItem = (
    targetSection,
    targetId = "",
    placement = "after",
    targetCategoryId = null
  ) => {
    if (!draggingId) return;

    const draggedItem = items.find(item => String(item.id) === String(draggingId));
    if (!draggedItem) return;

    const withoutDragged = items.filter(item => String(item.id) !== String(draggingId));
    const nextSections = {
      header: withoutDragged.filter(item => item.section === "header"),
      main: withoutDragged.filter(item => item.section === "main"),
      administration: withoutDragged.filter(item => item.section === "administration")
    };

    const nextDraggedItem = {
      ...draggedItem,
      section: targetSection,
      categoryId: targetCategoryId || null
    };
    const targetList = nextSections[targetSection];
    const targetIndex = targetId
      ? targetList.findIndex(item => String(item.id) === String(targetId))
      : targetList.length;
    const insertIndex =
      targetIndex >= 0
        ? targetIndex + (placement === "after" ? 1 : 0)
        : targetList.length;

    targetList.splice(insertIndex, 0, nextDraggedItem);

    const nextItems = ["header", "main", "administration"].flatMap(section =>
      nextSections[section].map((item, index) => ({
        ...item,
        section,
        position: index
      }))
    );

    setLocalItems(nextItems);
    persistOrder(nextItems);
    setDraggingId("");
    setDropTarget(null);
  };

  const renderItem = (item, horizontal = false, categoryId = null) => (
    <div
      key={item.id}
      className={`${classes.item} ${
        horizontal ? classes.headerItem : ""
      } ${
        String(draggingId) === String(item.id) ? classes.itemDragging : ""
      } ${
        dropTarget?.itemId === String(item.id) &&
        dropTarget?.placement === "before" &&
        !horizontal
          ? classes.itemDropBefore
          : ""
      } ${
        dropTarget?.itemId === String(item.id) &&
        dropTarget?.placement === "after" &&
        !horizontal
          ? classes.itemDropAfter
          : ""
      } ${
        dropTarget?.itemId === String(item.id) &&
        dropTarget?.placement === "before" &&
        horizontal
          ? classes.headerDropBefore
          : ""
      } ${
        dropTarget?.itemId === String(item.id) &&
        dropTarget?.placement === "after" &&
        horizontal
          ? classes.headerDropAfter
          : ""
      }`}
      draggable
      onDragStart={event => {
        event.dataTransfer.setData("text/plain", String(item.id));
        event.dataTransfer.effectAllowed = "move";
        setDraggingId(String(item.id));
      }}
      onDragEnd={() => {
        setDraggingId("");
        setDropTarget(null);
      }}
      onDragOver={event => {
        if (!draggingId || String(draggingId) === String(item.id)) return;
        event.preventDefault();
        event.stopPropagation();
        const bounds = event.currentTarget.getBoundingClientRect();
        const placement = horizontal
          ? event.clientX > bounds.left + bounds.width / 2
            ? "after"
            : "before"
          : event.clientY > bounds.top + bounds.height / 2
          ? "after"
          : "before";
        const nextTarget = {
          section: item.section,
          categoryId: item.categoryId || categoryId || null,
          itemId: String(item.id),
          placement
        };
        setDropTarget(prevState =>
          prevState?.section === nextTarget.section &&
          Number(prevState?.categoryId || 0) === Number(nextTarget.categoryId || 0) &&
          prevState?.itemId === nextTarget.itemId &&
          prevState?.placement === nextTarget.placement
            ? prevState
            : nextTarget
        );
      }}
      onDrop={event => {
        event.preventDefault();
        event.stopPropagation();
        moveItem(
          item.section,
          item.id,
          dropTarget?.placement || "after",
          item.categoryId || categoryId || null
        );
      }}
    >
      <DragIndicator className={classes.dragIcon} />
      <span className={classes.emoji}>{item.icon || "🔗"}</span>
      <div>
        <Typography className={classes.itemTitle}>{item.label}</Typography>
        <div className={classes.itemMeta}>
          <span>{typeLabels[item.type] || item.type}</span>
          {item.categoryId && (
            <span>
              {categories.find(category => Number(category.id) === Number(item.categoryId))
                ?.name || "Categoría"}
            </span>
          )}
          {!item.isActive && <span>Inactivo</span>}
          {item.type === "external" && (
            <>
              <Launch fontSize="small" />
              <span>{item.url}</span>
            </>
          )}
          {item.type === "embed" && <span>{item.url}</span>}
        </div>
      </div>
      <div className={classes.actions}>
        <IconButton size="small" onClick={() => openEditForm(item)}>
          <Edit fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setDeleteItem(item)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </div>
    </div>
  );

  const renderCategory = (category, horizontal = false) => {
    const categoryItems = groupedItems[category.section].filter(
      item => Number(item.categoryId) === Number(category.id)
    );
    const expanded = expandedCategories[category.id] !== false;

    return (
      <Paper
        key={category.id}
        className={`${classes.categoryCard} ${horizontal ? classes.headerCategoryCard : ""} ${
          String(draggingCategoryId) === String(category.id) ? classes.itemDragging : ""
        } ${
          categoryDropTarget?.categoryId === String(category.id) &&
          categoryDropTarget?.placement === "before" &&
          horizontal
            ? classes.headerDropBefore
            : ""
        } ${
          categoryDropTarget?.categoryId === String(category.id) &&
          categoryDropTarget?.placement === "after" &&
          horizontal
            ? classes.headerDropAfter
            : ""
        } ${
          categoryDropTarget?.categoryId === String(category.id) &&
          categoryDropTarget?.placement === "before" &&
          !horizontal
            ? classes.itemDropBefore
            : ""
        } ${
          categoryDropTarget?.categoryId === String(category.id) &&
          categoryDropTarget?.placement === "after" &&
          !horizontal
            ? classes.itemDropAfter
            : ""
        }`}
        elevation={0}
        draggable
        onDragStart={event => {
          event.dataTransfer.setData("text/category", String(category.id));
          event.dataTransfer.effectAllowed = "move";
          setDraggingCategoryId(String(category.id));
        }}
        onDragEnd={() => {
          setDraggingCategoryId("");
          setCategoryDropTarget(null);
        }}
        onDragOver={event => {
          if (!draggingCategoryId || String(draggingCategoryId) === String(category.id)) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          const bounds = event.currentTarget.getBoundingClientRect();
          const placement = horizontal
            ? event.clientX > bounds.left + bounds.width / 2
              ? "after"
              : "before"
            : event.clientY > bounds.top + bounds.height / 2
            ? "after"
            : "before";
          const nextTarget = {
            section: category.section,
            categoryId: String(category.id),
            placement
          };
          setCategoryDropTarget(prevState =>
            prevState?.section === nextTarget.section &&
            prevState?.categoryId === nextTarget.categoryId &&
            prevState?.placement === nextTarget.placement
              ? prevState
              : nextTarget
          );
        }}
        onDrop={event => {
          if (!draggingCategoryId) return;
          event.preventDefault();
          event.stopPropagation();
          moveCategory(category.section, category.id, categoryDropTarget?.placement || "after");
        }}
      >
        <div
          className={classes.categoryHeader}
          onClick={() =>
            setExpandedCategories(prevState => ({
              ...prevState,
              [category.id]: !expanded
            }))
          }
        >
          <span
            className={classes.categoryColor}
            style={{ backgroundColor: category.color || "#2F80ED" }}
          />
          <Typography variant="subtitle2" noWrap>{category.name}</Typography>
          <Typography variant="caption">{categoryItems.length}</Typography>
          <div>
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                openEditCategoryForm(category);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                setDeleteCategory(category);
              }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </div>
        </div>
        <Collapse in={expanded}>
          <div
            className={classes.categoryBody}
            onDragOver={event => {
              event.preventDefault();
              event.stopPropagation();
              if (draggingId) {
                const nextTarget = {
                  section: category.section,
                  categoryId: category.id,
                  itemId: "",
                  placement: "after"
                };
                setDropTarget(prevState =>
                  prevState?.section === nextTarget.section &&
                  Number(prevState?.categoryId || 0) === Number(nextTarget.categoryId || 0) &&
                  prevState?.itemId === nextTarget.itemId &&
                  prevState?.placement === nextTarget.placement
                    ? prevState
                    : nextTarget
                );
              }
            }}
            onDrop={event => {
              event.preventDefault();
              event.stopPropagation();
              moveItem(category.section, "", "after", category.id);
            }}
          >
            {categoryItems.length ? (
              categoryItems.map(item => renderItem(item, false, category.id))
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sin opciones en esta categoría
              </Typography>
            )}
          </div>
        </Collapse>
      </Paper>
    );
  };

  const renderSection = (section, horizontal = false) => (
    <div
      className={horizontal ? classes.headerSection : classes.section}
      onDragOver={event => {
        event.preventDefault();
        if (draggingId && !groupedItems[section].length) {
          setDropTarget(prevState => (prevState ? null : prevState));
        }
      }}
      onDrop={event => {
        event.preventDefault();
        moveItem(section);
      }}
    >
      <div className={classes.sectionHeader}>
        <Typography variant="subtitle1">{sectionLabels[section]}</Typography>
        <Typography variant="caption">{groupedItems[section].length} opciones</Typography>
      </div>
      <Divider />
      <div className={`${horizontal ? classes.headerItems : classes.items} ${classes.dropArea}`}>
        {categories
          .filter(category => category.section === section)
          .map(category => renderCategory(category, horizontal))}
        {groupedItems[section]
          .filter(item => !item.categoryId)
          .map(item => renderItem(item, horizontal))}
        {!groupedItems[section].length &&
          !categories.filter(category => category.section === section).length && (
          <div className={classes.empty}>Arrastra opciones aquí</div>
        )}
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleteItem ? `Eliminar ${deleteItem.label}?` : ""}
        open={Boolean(deleteItem)}
        onClose={() => setDeleteItem(null)}
        onConfirm={confirmDelete}
      >
        {deleteItem?.type === "builtin"
          ? "Esta opción del sistema se desactivará para la cuenta."
          : "Esta opción personalizada se eliminará del menú."}
      </ConfirmationModal>
      <ConfirmationModal
        title={deleteCategory ? `Eliminar categoría ${deleteCategory.name}?` : ""}
        open={Boolean(deleteCategory)}
        onClose={() => setDeleteCategory(null)}
        onConfirm={confirmDeleteCategory}
      >
        Las opciones dentro de esta categoría volverán a la sección principal.
      </ConfirmationModal>

      <ConfirmationModal
        title="Restablecer menú"
        open={resetMenuOpen}
        onClose={() => setResetMenuOpen(false)}
        onConfirm={handleResetMenu}
      >
        Se eliminarán categorías, opciones personalizadas y cambios de orden. El menú volverá a su estado original.
      </ConfirmationModal>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm">
        <form onSubmit={saveItem}>
          <DialogTitle>{form.id ? "Editar opción" : "Nueva opción"}</DialogTitle>
          <DialogContent dividers>
            <Typography className={classes.helper}>
              Las URL externas abren en una pestaña nueva. Los embebidos se muestran dentro del CRM.
            </Typography>
            <div className={classes.formGrid}>
              <TextField
                label="Icono"
                value={form.icon}
                onChange={handleFormChange("icon")}
                variant="outlined"
                margin="dense"
              />
              <TextField
                label="Nombre"
                value={form.label}
                onChange={handleFormChange("label")}
                variant="outlined"
                margin="dense"
                required
              />
              <FormControl variant="outlined" margin="dense" className={classes.fullWidth}>
                <InputLabel>Sección</InputLabel>
                <Select
                  label="Sección"
                  value={form.section}
                  onChange={handleFormChange("section")}
                >
                  <MenuItem value="main">Menú principal</MenuItem>
                  <MenuItem value="header">Header</MenuItem>
                  <MenuItem value="administration">Administración</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" margin="dense" className={classes.fullWidth}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  label="Categoría"
                  value={form.categoryId || NO_MENU_CATEGORY}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      categoryId:
                        event.target.value === NO_MENU_CATEGORY ? "" : event.target.value
                    }))
                  }
                >
                  <MenuItem value={NO_MENU_CATEGORY}>Sin categoría</MenuItem>
                  {categories
                    .filter(category => category.section === form.section)
                    .map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                margin="dense"
                className={classes.fullWidth}
                disabled={form.type === "builtin"}
              >
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo" value={form.type} onChange={handleFormChange("type")}>
                  <MenuItem value="external">URL externa</MenuItem>
                  <MenuItem value="embed">Embebido</MenuItem>
                  {form.type === "builtin" && <MenuItem value="builtin">Sistema</MenuItem>}
                </Select>
              </FormControl>
              {form.type !== "builtin" && (
                <TextField
                  className={classes.fullWidth}
                  label="URL"
                  value={form.url}
                  onChange={handleFormChange("url")}
                  variant="outlined"
                  margin="dense"
                  required
                />
              )}
              <FormControlLabel
                className={classes.fullWidth}
                control={
                  <Checkbox
                    color="primary"
                    checked={form.isActive}
                    onChange={handleFormChange("isActive")}
                  />
                }
                label="Activa"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm} variant="contained">
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={categoryFormOpen}
        onClose={() => setCategoryFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={saveCategory}>
          <DialogTitle>{categoryForm.id ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
          <DialogContent dividers>
            <div className={classes.formGrid}>
              <TextField
                label="Color"
                value={categoryForm.color}
                onChange={handleCategoryFormChange("color")}
                onBlur={event => {
                  if (!isValidHexColor(event.target.value)) return;
                  const color = normalizeHexColor(event.target.value);
                  setCategoryForm(prev => ({ ...prev, color }));
                  saveCustomColor(color);
                }}
                variant="outlined"
                margin="dense"
                className={classes.fullWidth}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        className={classes.colorAdornment}
                        style={{ backgroundColor: categoryForm.color }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => setCategoryColorPickerOpen(true)}
                    >
                      <Colorize />
                    </IconButton>
                  )
                }}
              />
              <ColorPicker
                open={categoryColorPickerOpen}
                currentColor={categoryForm.color}
                handleClose={() => setCategoryColorPickerOpen(false)}
                onChange={color => setCategoryForm(prev => ({ ...prev, color }))}
              />
              <TextField
                label="Nombre"
                value={categoryForm.name}
                onChange={handleCategoryFormChange("name")}
                variant="outlined"
                margin="dense"
                className={classes.fullWidth}
                required
              />
              <FormControl variant="outlined" margin="dense" className={classes.fullWidth}>
                <InputLabel>Sección</InputLabel>
                <Select
                  label="Sección"
                  value={categoryForm.section}
                  onChange={handleCategoryFormChange("section")}
                >
                  <MenuItem value="main">Menú principal</MenuItem>
                  <MenuItem value="header">Header</MenuItem>
                  <MenuItem value="administration">Administración</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                className={classes.fullWidth}
                control={
                  <Checkbox
                    color="primary"
                    checked={categoryForm.isActive}
                    onChange={handleCategoryFormChange("isActive")}
                  />
                }
                label="Activa"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryFormOpen(false)} variant="contained">
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <MainHeader>
        <div className={classes.headerContent}>
          <Title>Menú</Title>
          <MainHeaderButtonsWrapper>
            <FormControlLabel
              className={classes.headerSwitch}
              control={
                <Switch
                  color="primary"
                  checked={showAllNormalMenu}
                  onChange={handleToggleShowAllNormalMenu}
                  disabled={savingShowAllNormalMenu}
                />
              }
              label="Mostrar todo en Normal"
            />
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateForm}
            >
              Nuevo
            </Button>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={openCreateCategoryForm}
            >
              Categoría
            </Button>
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<Restore />}
              onClick={() => setResetMenuOpen(true)}
              disabled={saving}
            >
              Restablecer
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            {renderSection("header", true)}
            <div className={classes.sections}>
              {renderSection("main")}
              {renderSection("administration")}
            </div>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Menus;
